import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell, Field, FormError, SubmitButton } from "@/components/AuthShell";
import { Input } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { apiError, endpoints } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function GoogleButton() {
  const [loading, setLoading] = React.useState(false);
  const go = () => {
    setLoading(true);
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} disabled={loading} data-testid="login-google-btn"
      className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-md bg-white text-black font-medium text-sm hover:bg-white/90 transition-colors disabled:opacity-60">
      <svg width="18" height="18" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {loading ? "Redirecting…" : "Continue with Google"}
    </button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [unverified, setUnverified] = React.useState(false);
  const [resent, setResent] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setUnverified(false); setLoading(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (ex) {
      const m = apiError(ex);
      setErr(m);
      setUnverified(/not verified/i.test(m));
    } finally { setLoading(false); }
  };

  const resend = async () => {
    await endpoints.resendVerification(email).catch(() => {});
    setResent(true);
  };

  return (
    <AuthShell testid="login-page" eyebrow="Sign in" title={<>Welcome back to <span className="italic text-gold">Clara</span>.</>}
      subtitle="Access your deal origination workspace."
      footer={<>New to Clara Deals? <Link to="/register" className="text-gold hover:underline" data-testid="login-to-register">Create an account</Link></>}>
      <form onSubmit={submit} data-testid="login-form">
        <FormError msg={err} />
        {unverified && (
          <button type="button" onClick={resend} disabled={resent} className="mb-4 text-[12px] text-gold hover:underline" data-testid="login-resend-verification">
            {resent ? "Verification email re-sent — check your inbox." : "Resend verification email"}
          </button>
        )}
        <Field label="Email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" /></Field>
        <Field label="Password"><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" /></Field>
        <div className="flex justify-end -mt-2 mb-5">
          <Link to="/forgot-password" className="text-[12px] text-white/50 hover:text-gold" data-testid="login-forgot-link">Forgot password?</Link>
        </div>
        <SubmitButton loading={loading} testid="login-submit">Sign in</SubmitButton>
      </form>
      <div className="flex items-center gap-3 my-6 text-[10px] uppercase tracking-[0.2em] text-white/30">
        <span className="flex-1 h-px bg-white/10" /> or <span className="flex-1 h-px bg-white/10" />
      </div>
      <GoogleButton />
      <div className="mt-6 pt-5 border-t border-white/5 text-center">
        <Link to="/admin/login" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white/40 hover:text-gold transition-colors" data-testid="login-admin-access-link">
          Admin Access →
        </Link>
      </div>
    </AuthShell>
  );
}
